import React, {useCallback, useMemo, useState} from "react"
import PropTypes from "prop-types"
import {Box, Grid, IconButton, Stack, Typography, useTheme} from "@mui/material"
import {ArrowLeft} from "lucide-react"
import TabContent from "./ContainerTab/TabContent"
import Content from "../../components/Content"
import PrimaryButtonWidget from "../widgets/buttons/PrimaryButtonWidget"
import GhostButtonWidget from "../widgets/buttons/GhostbuttonWidget"

export const containerTabProgressHeaderProps = [
    {
        "key": "text",
        "type": "STRING",
        "label": "Titel",
        "size": 6,
    },
    {
        "key": "actionFirstBack",
        "type": "STRING",
        "label": "Aktion zurück (erste)",
        "size": 3,
    },
    {
        "key": "actionLastNext",
        "type": "STRING",
        "label": "Aktion weiter (letzte)",
        "size": 3,
    },
]

const ContainerTabProgressHeader = ({container, triggerAction}) => {
    const {children, text, actionFirstBack, actionLastNext} = container
    const theme = useTheme()

    const [activeTab, setActiveTab] = useState(0)

    const isLastEl = useMemo(
        () => (children?.length ?? 0) === (activeTab + 1),
        [activeTab, children?.length],
    )

    const hasNext = useMemo(() => {
        return actionLastNext || !isLastEl
    }, [isLastEl, actionLastNext])

    const hasBack = useMemo(() => {
        return actionFirstBack || activeTab !== 0
    }, [activeTab, actionFirstBack])

    const onClickBack = useCallback(() => {
        return actionFirstBack && activeTab === 0
            ? triggerAction(actionFirstBack)
            : setActiveTab(prevState => prevState - 1)
    }, [triggerAction, actionFirstBack, activeTab])

    const onClickNext = useCallback(() => {
        return actionLastNext && isLastEl
            ? triggerAction(actionLastNext)
            : setActiveTab(prevState => prevState + 1)
    }, [actionLastNext, triggerAction, isLastEl])

    return (
        <Stack spacing={3}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <IconButton
                    onClick={() => triggerAction("NAVIGATE_BACK")}
                    sx={theme.TABPROGRESSHEADER?.backButton}
                >
                    <ArrowLeft />
                </IconButton>
                <Typography variant="h2">{text}</Typography>
            </Box>

            <Box sx={{display: "flex", gap: 1}}>
                {children.map((child, index) => (
                    <Box
                        key={child.id}
                        sx={index <= activeTab
                            ? theme.TABPROGRESSHEADER?.progressSegmentActive
                            : theme.TABPROGRESSHEADER?.progressSegmentDefault
                        }
                    />
                ))}
            </Box>

            {children.map((child, index) => (
                <TabContent key={child.id} value={activeTab} index={index}>
                    <Content child={child} />
                </TabContent>
            ))}

            <Grid container justifyContent="space-between">
                {hasBack && <Grid>
                    <GhostButtonWidget widget={{
                        text: "zurück",
                        startIcon: "arrow-left",
                        onClick: onClickBack,
                    }} />
                </Grid>}
                {!hasBack && <Box />}
                {hasNext && <Grid>
                    <PrimaryButtonWidget widget={{
                        text: isLastEl ? "Fertig" : "weiter",
                        endIcon: "arrow-right",
                        onClick: onClickNext,
                    }} />
                </Grid>}
            </Grid>
        </Stack>
    )
}

ContainerTabProgressHeader.propTypes = {
    container: PropTypes.object,
}

export default ContainerTabProgressHeader
